import * as actionTypes from './actionTypes';
import {
  TrackingDomain,
  SendingDomain,
  EspAccount,
  AndroidAssetLink,
  AppSiteAssociations,
  AssetLink,
  LinkShorteningTemplateId,
} from './types';

/*-------------*/
/* MailDomains Fetch Actions                                            */
/*------------*/
export const fetchMailDomainsRequest = () => ({
  type: actionTypes.FETCH_MAIL_DOMAINS_REQUEST,
});

export const fetchMailDomainsError = (error: string) => ({
  type: actionTypes.FETCH_MAIL_DOMAINS_ERROR,
  error,
});

export const fetchMailDomainsResponse = (
  espAccounts: EspAccount[],
  sendingDomains: SendingDomain[],
  trackingDomains: TrackingDomain[],
  verificationErrors: string[],
  linkShorteningTemplateIds: LinkShorteningTemplateId[],
  defaultSmsLinkShorteningDomain: string,
) => ({
  type: actionTypes.FETCH_MAIL_DOMAINS_RESPONSE,
  espAccounts,
  sendingDomains,
  trackingDomains,
  verificationErrors,
  linkShorteningTemplateIds,
  defaultSmsLinkShorteningDomain,
});

/*-------------*/
/* SendingDomain Create Actions                                            */
/*------------*/
export const createSendingDomainRequest = (sendingDomain: string, espAccountId: string) => ({
  type: actionTypes.CREATE_SENDING_DOMAIN_REQUEST,
  sendingDomain,
  espAccountId,
});

export const createSendingDomainError = (error: string) => ({
  type: actionTypes.CREATE_SENDING_DOMAIN_ERROR,
  error,
});

export const createSendingDomainResponse = (sendingDomain: SendingDomain) => ({
  type: actionTypes.CREATE_SENDING_DOMAIN_RESPONSE,
  sendingDomain,
});

/*-------------*/
/* SendingDomain Edit Actions                                            */
/*------------*/
export const editSendingDomainRequest = (
  sendingDomain: SendingDomain,
  successMessage = 'Successfully edited the sending domain',
) => ({
  type: actionTypes.EDIT_SENDING_DOMAIN_REQUEST,
  sendingDomain,
  successMessage,
});

export const editSendingDomainError = (error: string) => ({
  type: actionTypes.EDIT_SENDING_DOMAIN_ERROR,
  error,
});

export const editSendingDomainResponse = (sendingDomains: Array<SendingDomain>) => ({
  type: actionTypes.EDIT_SENDING_DOMAIN_RESPONSE,
  sendingDomains,
});

/*-------------*/
/* SendingDomain Delete Actions                                            */
/*------------*/
export const deleteSendingDomainRequest = (sendingDomainId: number) => ({
  type: actionTypes.DELETE_SENDING_DOMAIN_REQUEST,
  sendingDomainId,
});

export const deleteSendingDomainError = (error: string) => ({
  type: actionTypes.DELETE_SENDING_DOMAIN_ERROR,
  error,
});

export const deleteSendingDomainResponse = (sendingDomainId: number) => ({
  type: actionTypes.DELETE_SENDING_DOMAIN_RESPONSE,
  sendingDomainId,
});

/*-------------*/
/* TrackingDomain Create Actions                                            */
/*------------*/
export const createTrackingDomainRequest = (trackingDomain: string) => ({
  type: actionTypes.CREATE_TRACKING_DOMAIN_REQUEST,
  trackingDomain,
});

export const createTrackingDomainError = (error: string) => ({
  type: actionTypes.CREATE_TRACKING_DOMAIN_ERROR,
  error,
});

export const createTrackingDomainResponse = (trackingDomain: TrackingDomain) => ({
  type: actionTypes.CREATE_TRACKING_DOMAIN_RESPONSE,
  trackingDomain,
});

/*-------------*/
/* TrackingDomain Update Actions                                            */
/*------------*/
export const editTrackingDomainRequest = (trackingDomain: TrackingDomain) => ({
  type: actionTypes.EDIT_TRACKING_DOMAIN_REQUEST,
  trackingDomain,
});

export const editTrackingDomainError = (error: string) => ({
  type: actionTypes.EDIT_TRACKING_DOMAIN_ERROR,
  error,
});

export const editTrackingDomainResponse = (trackingDomains: Array<TrackingDomain>) => ({
  type: actionTypes.EDIT_TRACKING_DOMAIN_RESPONSE,
  trackingDomains,
});

/*-------------*/
/* TrackingDomain Update Actions                                            */
/*------------*/
export const setDefaultTrackingDomainRequest = (trackingDomain: TrackingDomain) => ({
  type: actionTypes.SET_DEFAULT_TRACKING_DOMAIN_REQUEST,
  trackingDomain,
});

export const setDefaultTrackingDomainError = (error: string) => ({
  type: actionTypes.SET_DEFAULT_TRACKING_DOMAIN_ERROR,
  error,
});

export const setDefaultTrackingDomainResponse = (trackingDomains: Array<TrackingDomain>) => ({
  type: actionTypes.SET_DEFAULT_TRACKING_DOMAIN_RESPONSE,
  trackingDomains,
});

/*-------------*/
/* TrackingDomain Update Actions                                            */
/*------------*/
export const deleteTrackingDomainRequest = (trackingDomainName: string) => ({
  type: actionTypes.DELETE_TRACKING_DOMAIN_REQUEST,
  trackingDomainName,
});

export const deleteTrackingDomainError = (error: string) => ({
  type: actionTypes.DELETE_TRACKING_DOMAIN_ERROR,
  error,
});

export const deleteTrackingDomainResponse = (trackingDomains: Array<TrackingDomain>) => ({
  type: actionTypes.DELETE_TRACKING_DOMAIN_RESPONSE,
  trackingDomains,
});

/*-------------*/
/* Fetch DeepLinks Actions                                            */
/*------------*/
export const fetchDeepLinksRequest = () => ({
  type: actionTypes.FETCH_DEEP_LINKS_REQUEST,
});

export const fetchDeepLinksError = (error: string) => ({
  type: actionTypes.FETCH_DEEP_LINKS_ERROR,
  error,
});

export const fetchDeepLinksResponse = (
  assetLinks: Array<AndroidAssetLink>,
  appSiteAssociations: Array<AppSiteAssociations>,
) => ({
  type: actionTypes.FETCH_DEEP_LINKS_RESPONSE,
  assetLinks,
  appSiteAssociations,
});

/*-------------*/
/* DeepLink Path Create Actions                                            */
/*------------*/
export const createDeepLinkPathRequest = (
  appDomain: string,
  domain: string,
  organizationId: number,
  json: string,
) => ({
  type: actionTypes.CREATE_DEEP_LINK_PATH_REQUEST,
  appDomain,
  domain,
  organizationId,
  json,
});

export const createDeepLinkPathError = (error: string) => ({
  type: actionTypes.CREATE_DEEP_LINK_PATH_ERROR,
  error,
});

export const createDeepLinkPathResponse = (appSiteAssociations: Array<AppSiteAssociations>) => ({
  type: actionTypes.CREATE_DEEP_LINK_PATH_RESPONSE,
  appSiteAssociations,
});

/*-------------*/
/* DeepLink Path Enable Actions                                            */
/*------------*/
export const enableDeepLinkPathRequest = (
  appSiteAssociation: AppSiteAssociations,
  hasActiveLegacyDomain: boolean,
) => ({
  type: actionTypes.ENABLE_DEEP_LINK_PATH_REQUEST,
  appSiteAssociation,
  hasActiveLegacyDomain,
});

export const enableDeepLinkPathError = (error: string) => ({
  type: actionTypes.ENABLE_DEEP_LINK_PATH_ERROR,
  error,
});

export const enableDeepLinkPathResponse = (appSiteAssociations: Array<AppSiteAssociations>) => ({
  type: actionTypes.ENABLE_DEEP_LINK_PATH_RESPONSE,
  appSiteAssociations,
});

/*-------------*/
/* DeepLink Path Edit Actions                                            */
/*------------*/
export const editDeepLinkPathRequest = (
  appDomain: string,
  domain: string,
  organizationId: number,
  json: string,
) => ({
  type: actionTypes.EDIT_DEEP_LINK_PATH_REQUEST,
  appDomain,
  domain,
  organizationId,
  json,
});

export const editDeepLinkPathError = (error: string) => ({
  type: actionTypes.EDIT_DEEP_LINK_PATH_ERROR,
  error,
});

export const editDeepLinkPathResponse = (appSiteAssociations: Array<AppSiteAssociations>) => ({
  type: actionTypes.EDIT_DEEP_LINK_PATH_RESPONSE,
  appSiteAssociations,
});

/*-------------*/
/* Upsert Android Asset Links Actions                                            */
/*------------*/
export const upsertAndroidAssetLinksRequest = (
  assetLinks: Array<AssetLink>,
  domain: string,
  organizationId: number,
) => ({
  type: actionTypes.UPSERT_ANDROID_ASSET_LINKS_REQUEST,
  assetLinks,
  domain,
  organizationId,
});

export const upsertAndroidAssetLinksError = (error: string) => ({
  type: actionTypes.UPSERT_ANDROID_ASSET_LINKS_ERROR,
  error,
});

export const upsertAndroidAssetLinksResponse = (assetLinks: Array<AndroidAssetLink>) => ({
  type: actionTypes.UPSERT_ANDROID_ASSET_LINKS_RESPONSE,
  assetLinks,
});
